import React, { useEffect, useMemo, useReducer, useRef } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import U2000SaveCancelDialog from "../../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000InputCalendarCustom from "../../../../u2000Core/components/formControls/input/U2000InputCalendarCustom";
import U2000InputCurrency from "../../../../u2000Core/components/formControls/input/U2000InputCurrency";
import U2000InputDecimal from "../../../../u2000Core/components/formControls/input/U2000InputDecimal";
import U2000InputText from "../../../../u2000Core/components/formControls/input/U2000InputText";
import U2000RadioGroupString from "../../../../u2000Core/components/formControls/radio/U2000RadioGroupString";
import U2000Panel from "../../../../u2000Core/components/layout/U2000Panel";
import u2000FormState, {
  U2000FormStateEnumEditMode,
  U2000FormStateEnumState,
  type U2000FormState,
} from "../../../../u2000Core/reducer/formReducer/U2000FormState";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import {
  u2000ShiftCivilYmd,
  u2000TodayOrgYmd,
} from "../../../../u2000Core/tools/U2000DateTimeHelpers";
import { NS } from "../../../U3101";
import {
  type U3101RemunerationType,
  type U3101SalaryHistory,
  type U3101SalaryHistoryApplyMode,
  type U3101SalaryHistoryEntry,
  u3101SalaryHistoryCurrentEntry,
} from "../../../U3101Service";

export type U3101SalaryHistoryDlgRow = {
  applyMode: U3101SalaryHistoryApplyMode;
  effectiveDate: string;
  remunerationType: U3101RemunerationType;
  hourRate: number | null;
  hourlyBase: number | null;
  comment: string;
};

export interface U3101SalaryHistoryEntryDlgProps {
  isOpen: boolean;
  dialogKind: "create" | "edit";
  history: U3101SalaryHistory | null;
  editEntry?: U3101SalaryHistoryEntry;
  busy?: boolean;
  onClose: () => void;
  onSave: (row: U3101SalaryHistoryDlgRow) => void | Promise<void>;
};

type ValidateCtx = {
  dialogKind: "create" | "edit";
  todayYmd: string;
  t: TFunction;
  touched: Record<string, boolean>;
  submitted: boolean;
};

function validateSalaryHistoryDlgRow(data: U3101SalaryHistoryDlgRow, ctx: ValidateCtx): Record<string, string> {
  const errors: Record<string, string> = {};
  const show = (field: string) => ctx.submitted || Boolean(ctx.touched[field]);

  if (show("hourRate")) {
    const rate = data.hourRate;
    if (
      rate === null ||
      rate === undefined ||
      Number.isNaN(Number(rate)) ||
      Number(rate) < 0 ||
      Number(rate) > 999.99
    ) {
      errors.hourRate = ctx.t("errHourRateRange", { ns: NS });
    }
  }

  if (show("hourlyBase")) {
    if (
      data.hourlyBase !== null &&
      data.hourlyBase !== undefined &&
      (Number.isNaN(Number(data.hourlyBase)) || Number(data.hourlyBase) < 0)
    ) {
      errors.hourlyBase = ctx.t("errHourlyBaseNegative", { ns: NS });
    }
  }

  const needsDate = ctx.dialogKind === "edit" || data.applyMode === "later";
  if (needsDate && show("effectiveDate")) {
    const ed = data.effectiveDate?.trim() ?? "";
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ed) || ed <= ctx.todayYmd) {
      errors.effectiveDate = ctx.t("errSalaryHistoryEffectiveDateLater", { ns: NS });
    }
  }

  return errors;
}

function isSalaryHistoryDlgRowComplete(
  data: U3101SalaryHistoryDlgRow,
  dialogKind: "create" | "edit",
  todayYmd: string,
  t: TFunction,
): boolean {
  const errors = validateSalaryHistoryDlgRow(data, {
    dialogKind,
    todayYmd,
    t,
    touched: {},
    submitted: true,
  });
  return Object.keys(errors).length === 0;
}

function emptyCreateRow(): U3101SalaryHistoryDlgRow {
  return {
    applyMode: "now",
    effectiveDate: u2000TodayOrgYmd(),
    remunerationType: "Hourly",
    hourRate: null,
    hourlyBase: null,
    comment: "",
  };
}

function createRowFromHistory(history: U3101SalaryHistory | null): U3101SalaryHistoryDlgRow {
  const row = emptyCreateRow();
  const current = u3101SalaryHistoryCurrentEntry(history);
  if (!current) return row;
  return {
    ...row,
    remunerationType: current.remunerationType,
    hourlyBase: current.hourlyBase,
  };
}

function rowFromEntry(entry: U3101SalaryHistoryEntry): U3101SalaryHistoryDlgRow {
  return {
    applyMode: "later",
    effectiveDate: entry.effectiveDate,
    remunerationType: entry.remunerationType,
    hourRate: entry.hourRate,
    hourlyBase: entry.hourlyBase,
    comment: entry.comment ?? "",
  };
}

const dlgInitialFormState: U2000FormState<U3101SalaryHistoryDlgRow> = {
  ...u2000IntFormState,
  current: emptyCreateRow(),
  old: null as unknown as U3101SalaryHistoryDlgRow,
  state: U2000FormStateEnumState.EDIT,
  editMode: U2000FormStateEnumEditMode.EDIT,
};

const U3101SalaryHistoryEntryDlg: React.FC<U3101SalaryHistoryEntryDlgProps> = ({
  isOpen,
  dialogKind,
  history,
  editEntry,
  busy = false,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const [dialogFormState, dialogFormDispatch] = useReducer(u2000FormState, dlgInitialFormState);
  const wasOpenRef = useRef(false);

  const todayYmd = useMemo(() => u2000TodayOrgYmd(), []);
  const minLaterYmd = useMemo(() => u2000ShiftCivilYmd(todayYmd, 1) ?? todayYmd, [todayYmd]);

  const dlgForm = useU2000FormStateHandlers<U3101SalaryHistoryDlgRow>({
    formState: dialogFormState,
    formStateDispatch: dialogFormDispatch,
    businessHandlers: {
      validateForm: (data) =>
        validateSalaryHistoryDlgRow(data, {
          dialogKind,
          todayYmd,
          t,
          touched: dialogFormState.touched,
          submitted: dialogFormState.isSubmitted,
        }),
    },
  });

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      dlgForm.setEditMode(U2000FormStateEnumEditMode.EDIT);
      dlgForm.setSubmitted(false);
      dlgForm.setErrors({});
      dlgForm.setData(
        dialogKind === "edit" && editEntry ? rowFromEntry(editEntry) : createRowFromHistory(history),
      );
    }
    wasOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- réinitialiser à l’ouverture uniquement
  }, [isOpen, dialogKind, editEntry, history]);

  const row = dlgForm.getData();
  const applyMode = row?.applyMode ?? "now";

  useEffect(() => {
    if (!isOpen || dialogKind !== "create") return;
    const data = dlgForm.getData();
    if (data.applyMode === "now" && data.effectiveDate !== todayYmd) {
      dlgForm.setData({ ...data, effectiveDate: todayYmd });
    } else if (data.applyMode === "later" && data.effectiveDate <= todayYmd) {
      dlgForm.setData({ ...data, effectiveDate: minLaterYmd });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync date selon applyMode
  }, [applyMode, isOpen, dialogKind, todayYmd, minLaterYmd]);

  const remunerationOptions = useMemo(
    () => [
      { value: "Hourly", label: t("valRemunerationTypeHourly", { ns: NS }) },
      { value: "Salary", label: t("valRemunerationTypeSalary", { ns: NS }) },
    ],
    [t],
  );

  const applyModeOptions = useMemo(
    () => [
      { value: "now", label: t("valSalaryHistoryApplyNow", { ns: NS }) },
      { value: "later", label: t("valSalaryHistoryApplyLater", { ns: NS }) },
    ],
    [t],
  );

  const showEffectiveDate = dialogKind === "edit" || applyMode === "later";
  const formValid = isSalaryHistoryDlgRowComplete(row, dialogKind, todayYmd, t) && !busy;

  const handleClose = (action: "save" | "cancel") => {
    if (action === "cancel") {
      onClose();
      return;
    }
    dlgForm.setSubmitted(true);
    const errors = validateSalaryHistoryDlgRow(dlgForm.getData(), {
      dialogKind,
      todayYmd,
      t,
      touched: dlgForm.getTouched(),
      submitted: true,
    });
    dlgForm.setErrors(errors);
    if (Object.keys(errors).length > 0 || busy) return;
    void onSave(dlgForm.getData());
  };

  if (!isOpen) return null;

  return (
    <U2000SaveCancelDialog
      isOpen={isOpen}
      title={
        dialogKind === "create"
          ? t("dlgSalaryHistoryCreateTitle", { ns: NS })
          : t("dlgSalaryHistoryEditTitle", { ns: NS })
      }
      onClose={handleClose}
      isValid={formValid}
      width="max-w-xl"
      contentOverflowVisible
    >
      <div className="flex flex-col gap-6 py-1">
        {dialogKind === "create" ? (
          <U2000Panel title={t("lblSalaryHistoryApplyMode", { ns: NS })} titleAlign="left">
            <div className="mt-2 px-1">
              <U2000RadioGroupString
                orientation="vertical"
                formStateHandlers={dlgForm as never}
                fieldName="applyMode"
                radioGroupName="u3101SalaryHistoryDlg-applyMode"
                options={applyModeOptions}
                required
                readonly={busy}
              />
            </div>
          </U2000Panel>
        ) : null}

        {showEffectiveDate ? (
          <div className="px-0.5 pb-1">
            <U2000InputCalendarCustom
              formStateHandlers={dlgForm as never}
              fieldName="effectiveDate"
              label={t("lblSalaryHistoryEffectiveDate", { ns: NS })}
              required
              dateFormat="yyyy-MM-dd"
              popupZIndex={10050}
              popupPosition="auto"
              readonly={busy}
            />
          </div>
        ) : null}

        <U2000Panel
          title={t("lblRemunerationType", { ns: NS })}
          titleAlign="left"
          className={`flex min-w-0 flex-col${showEffectiveDate ? " mt-3" : ""}`}
        >
          <div className="mt-2 grid grid-cols-2 items-start gap-4 px-1">
            <div className="flex flex-col gap-2">
              <U2000RadioGroupString
                orientation="vertical"
                formStateHandlers={dlgForm as never}
                fieldName="remunerationType"
                radioGroupName="u3101SalaryHistoryDlg-remunerationType"
                options={remunerationOptions}
                required
                readonly={busy}
              />
            </div>
            <div className="flex flex-col gap-2">
              <U2000InputCurrency
                fieldName="hourRate"
                label={t("lblHourRate", { ns: NS })}
                formStateHandlers={dlgForm as never}
                required
                allowNegative={false}
                decimalDigits={2}
                maxValue={999.99}
                readonly={busy}
                disabled={busy}
              />
              <U2000InputDecimal
                fieldName="hourlyBase"
                label={t("lblHourlyBase", { ns: NS })}
                formStateHandlers={dlgForm as never}
                required={false}
                allowNegative={false}
                decimalDigits={2}
                textAlign="right"
                readonly={busy}
              />
            </div>
          </div>
        </U2000Panel>

        <div className="px-0.5 pt-1">
          <U2000InputText
            fieldName="comment"
            label={t("lblSalaryHistoryComment", { ns: NS })}
            formStateHandlers={dlgForm as never}
            required={false}
            readonly={busy}
          />
        </div>
      </div>
    </U2000SaveCancelDialog>
  );
};

export default U3101SalaryHistoryEntryDlg;
